import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
  type UserDataInput,
  type BankDetails,
  type User,
  Salutation,
} from "@/shared/generated/graphql";
import { useNotifications } from "@/shared/composables/useNotifications.composable";
import { i18n } from "@/shared/i18n/i18n";
import { PREFERRED_LOCALE_LOCAL_STORAGE_KEY } from "@/shared/i18n/preferredLocaleLocalStorageKey";
import {
  type CustomerBase,
  mapToCustomerBaseType,
} from "@/shared/@types/CustomerBase";
import type { UserData } from "@/shared/@types/UserData";
import { defaultLocale } from "@/shared/i18n/availableLocales";
import { getBrowserLocale } from "@/shared/i18n/setupI18n";
import type { UserLocaleCode } from "@/shared/@types/UserLocaleCode";
import {
  clearImpersonationCookie,
  getImpersonatedUserIdFromCookie,
} from "@/shared/helper/impersonationCookie.helper";
import { getCurrentUser } from "@/shared/services/getCurrentUser.service";
import { updateEmail as updateEmailService } from "@/shared/services/updateEmail.service";
import { updatePassword as updatePasswordService } from "@/shared/services/updatePassword.service";
import { updateUserData as updateUserDataService } from "@/shared/services/updateUserData.service";
import { updateBankDetails as updateBankDetailsService } from "@/shared/services/updateBankDetails.service";

export const useAccountStore = defineStore("Account", () => {
  const { addErrorSnackbar } = useNotifications();

  const user = ref<undefined | User>(undefined);
  const updatingUser = ref<boolean>(false);
  const updatingUserError = ref<string | undefined>(undefined);
  const fetchingUser = ref<boolean>(false);
  const fetchingUserError = ref<string | undefined>(undefined);
  const userInitiallyFetched = ref<boolean>(false);
  const allCustomers = ref<Array<CustomerBase>>([]);

  const updateEmail = async (email: string) => {
    try {
      updatingUserError.value = undefined;
      updatingUser.value = true;
      await updateEmailService(email);
    } catch (error) {
      updatingUserError.value = error as string;
    } finally {
      updatingUser.value = false;
    }
  };

  const updatePassword = async (password: string) => {
    try {
      updatingUserError.value = undefined;
      updatingUser.value = true;
      await updatePasswordService(password);
    } catch (error) {
      updatingUserError.value = error as string;
    } finally {
      updatingUser.value = false;
    }
  };

  const updateUserData = async (userData: UserDataInput) => {
    try {
      updatingUserError.value = undefined;
      updatingUser.value = true;
      await updateUserDataService(userData);
    } catch (error) {
      updatingUserError.value = error as string;
    } finally {
      updatingUser.value = false;
    }
  };

  const updateBankDetails = async (details: BankDetails) => {
    try {
      updatingUserError.value = undefined;
      updatingUser.value = true;
      await updateBankDetailsService(details);
    } catch (error) {
      updatingUserError.value = error as string;
    } finally {
      updatingUser.value = false;
    }
  };

  const fetchUser = async () => {
    try {
      fetchingUserError.value = undefined;
      fetchingUser.value = true;
      const { errors, user: userResult } = await getCurrentUser();
      if (errors.length > 0) {
        fetchingUserError.value = errors[0] as string;
      }
      user.value = userResult || undefined;
      if (user.value)
        allCustomers.value = user.value.permissions.customers.map((customer) =>
          mapToCustomerBaseType(customer, user.value!),
        );

      // If the user ID of the impersonation cookie is not found, the original (impersonating) user ID
      // is returned by the backend without an error, therefore we handle the case here
      const impersonatedUserId = getImpersonatedUserIdFromCookie();
      if (impersonatedUserId && user.value?.userId !== impersonatedUserId) {
        clearImpersonationCookie();
        addErrorSnackbar(i18n.t("dashboard.superuser.error"));
      }
    } catch (error) {
      fetchingUserError.value = error as string;
    } finally {
      fetchingUser.value = false;
      if (!userInitiallyFetched.value) userInitiallyFetched.value = true;
    }
  };

  // --- Language ---
  const setUserLanguage = (localeCode: UserLocaleCode) => {
    if (
      typeof localStorage !== "undefined" &&
      localStorage.getItem(PREFERRED_LOCALE_LOCAL_STORAGE_KEY) ===
        i18n.locale.value
    )
      return;

    // In case there is no user language in the local storage but the user has a language set in the account info, store it in the local storage.
    // We do not want to overwrite the language in the storage, as caching might cause the user to have a different language in the storage than in the account info.
    if (
      typeof localStorage !== "undefined" &&
      !localStorage.getItem(PREFERRED_LOCALE_LOCAL_STORAGE_KEY) &&
      localeCode
    ) {
      localStorage.setItem(PREFERRED_LOCALE_LOCAL_STORAGE_KEY, localeCode);
    }

    if (localeCode !== i18n.locale.value) {
      i18n.locale.value = localeCode || defaultLocale.code;
      if (typeof window !== "undefined") window.location.reload();
    }
  };

  // Used instead of the user language (e.g. for OCI users). The preferred locale in the local storage is ignored and not overwritten,
  // so it stays untouched for non-OCI sessions. No reload is needed, as it is applied before the route is rendered.
  const setBrowserLanguage = () => {
    const localeCode = getBrowserLocale() ?? defaultLocale.code;
    if (localeCode === i18n.locale.value) return;

    i18n.locale.value = localeCode;
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", localeCode);
    }
  };

  const email = computed(() => user.value?.email);
  const bankDetails = computed(() => user.value?.bankDetails);
  const addressDetails = computed(
    () => user.value?.addressDetails || undefined,
  );
  const isUpdatingUser = computed(() => updatingUser.value);
  const isFetchingUser = computed(() => fetchingUser.value);
  const isUserInitiallyFetched = computed(() => userInitiallyFetched.value);
  const hasErrorWhileUpdatingUser = computed(
    () => updatingUserError.value !== undefined,
  );
  const hasErrorWhileFetchingUser = computed(
    () => fetchingUserError.value !== undefined,
  );
  const hasBankDetails = computed(
    () =>
      user.value?.bankDetails !== undefined && user.value?.bankDetails !== null,
  );
  const hasAddressDetails = computed(() => !!user.value?.addressDetails);
  const canStoreBankDetails = computed(
    () => user.value?.capabilities.canStoreBankDetails || false,
  );
  const canStoreAddressDetails = computed(
    () => user.value?.capabilities.canStoreAddressDetails || false,
  );
  const sapCustomerExists = computed(
    () => canStoreAddressDetails.value || canStoreBankDetails.value,
  );
  const hasMultipleCustomers = computed(() => allCustomers.value.length > 1);

  const userData = computed<UserData>(() => ({
    salutation: user.value?.salutation || Salutation.Unknown,
    firstName: user.value?.firstName || "",
    lastName: user.value?.lastName || "",
    email: email.value || "",
  }));

  const resetData = () => {
    user.value = undefined;
    updatingUser.value = false;
    updatingUserError.value = undefined;
    fetchingUser.value = false;
    fetchingUserError.value = undefined;
    userInitiallyFetched.value = false;
    allCustomers.value = [];
  };

  return {
    user,
    updatingUser,
    updatingUserError,
    fetchingUser,
    fetchingUserError,
    userInitiallyFetched,
    allCustomers,
    email,
    bankDetails,
    addressDetails,
    isUpdatingUser,
    isFetchingUser,
    isUserInitiallyFetched,
    hasErrorWhileUpdatingUser,
    hasErrorWhileFetchingUser,
    hasBankDetails,
    hasAddressDetails,
    canStoreBankDetails,
    canStoreAddressDetails,
    sapCustomerExists,
    hasMultipleCustomers,
    userData,
    updateEmail,
    updatePassword,
    updateUserData,
    updateBankDetails,
    setUserLanguage,
    setBrowserLanguage,
    fetchUser,
    resetData,
  };
});
